import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress, Typography } from '@mui/material';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { WorkflowProvider } from './hooks/useWorkflow';
import { NetworkStatusProvider } from './hooks/useNetworkStatus';
import { SupervisionProvider } from './contexts/SupervisionContext';
import { OfflineIndicator } from './components/OfflineIndicator';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import { CriticalErrorBoundary, FormErrorBoundary } from './components/ErrorBoundary';
import { offlineStorage } from './services/offlineStorage';
import { errorHandler } from './services/errorHandler';
import queryClient from './lib/queryClient';
import theme from './theme';
import './styles/printStyles.css';

// Lazy load route components for better performance
const Login = React.lazy(() => import('./components/Login').then(module => ({ default: module.Login })));
const Dashboard = React.lazy(() => import('./pages/Dashboard').then(module => ({ default: module.Dashboard })));
const Help = React.lazy(() => import('./pages/Help').then(module => ({ default: module.Help })));
const SetPassword = React.lazy(() => import('./components/SetPassword').then(module => ({ default: module.SetPassword })));
const ForcedPasswordChange = React.lazy(() => import('./components/ForcedPasswordChange').then(module => ({ default: module.ForcedPasswordChange })));
const PasswordReset = React.lazy(() => import('./pages/PasswordReset'));
// Help Module Pages
const PatientManagementModule = React.lazy(() => import('./pages/help/PatientManagementModule').then(module => ({ default: module.PatientManagementModule })));
const EmployeeManagementModule = React.lazy(() => import('./pages/help/EmployeeManagementModule').then(module => ({ default: module.EmployeeManagementModule })));
const SupervisionFormsModule = React.lazy(() => import('./pages/help/SupervisionFormsModule').then(module => ({ default: module.SupervisionFormsModule })));
const ReportingAnalyticsModule = React.lazy(() => import('./pages/help/ReportingAnalyticsModule').then(module => ({ default: module.ReportingAnalyticsModule })));
const UserManagementModule = React.lazy(() => import('./pages/help/UserManagementModule').then(module => ({ default: module.UserManagementModule })));
const AuditComplianceModule = React.lazy(() => import('./pages/help/AuditComplianceModule').then(module => ({ default: module.AuditComplianceModule })));

// Help Topic Pages
const GettingStartedTopic = React.lazy(() => import('./pages/help/GettingStartedTopic').then(module => ({ default: module.GettingStartedTopic })));
const AuthenticationSecurityTopic = React.lazy(() => import('./pages/help/AuthenticationSecurityTopic').then(module => ({ default: module.AuthenticationSecurityTopic })));
const WorkingOfflineTopic = React.lazy(() => import('./pages/help/WorkingOfflineTopic').then(module => ({ default: module.WorkingOfflineTopic })));
const CommonWorkflowsTopic = React.lazy(() => import('./pages/help/CommonWorkflowsTopic').then(module => ({ default: module.CommonWorkflowsTopic })));
const TroubleshootingTopic = React.lazy(() => import('./pages/help/TroubleshootingTopic').then(module => ({ default: module.TroubleshootingTopic })));
const BestPracticesTopic = React.lazy(() => import('./pages/help/BestPracticesTopic').then(module => ({ default: module.BestPracticesTopic })));

// Article Listing Page
const ArticleListingPage = React.lazy(() => import('./pages/ArticleListingPage').then(module => ({ default: module.ArticleListingPage })));
const ArticleDetailPage = React.lazy(() => import('./pages/ArticleDetailPage').then(module => ({ default: module.ArticleDetailPage })));

// Loading fallback component for route-level lazy loading
const RouteLoadingFallback: React.FC = () => (
  <Box 
    component="main"
    display="flex" 
    flexDirection="column"
    justifyContent="center" 
    alignItems="center" 
    minHeight="100vh"
    gap={2}
    role="main"
    aria-label="Loading application content"
  >
    <CircularProgress size={48} />
    <Typography variant="body1" color="text.secondary">
      Loading application...
    </Typography>
  </Box>
);

const AppContent: React.FC = () => {
  const { user, loading, passwordResetRequired, handlePasswordChanged } = useAuth();

  if (loading) {
    return <RouteLoadingFallback />;
  }

  // If user is authenticated but needs to change password, show forced password change
  if (user && passwordResetRequired) {
    const reason = user.passwordLastChanged ? 'admin-reset' : 'first-time';
    return (
      <Suspense fallback={<RouteLoadingFallback />}>
        <ForcedPasswordChange
          onPasswordChanged={handlePasswordChanged}
          reason={reason}
          userEmail={user.email}
        />
      </Suspense>
    );
  }

  return (
    <CriticalErrorBoundary>
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route 
            path="/set-password/:token" 
            element={
              <FormErrorBoundary formName="SetPassword">
                <SetPassword />
              </FormErrorBoundary>
            } 
          />
          <Route 
            path="/login" 
            element={
              <FormErrorBoundary formName="Login">
                <Login />
              </FormErrorBoundary>
            } 
          />
          <Route 
            path="/reset-password/:token" 
            element={
              <FormErrorBoundary formName="PasswordReset">
                <PasswordReset />
              </FormErrorBoundary>
            } 
          />
          <Route 
            path="/reset-password" 
            element={
              <FormErrorBoundary formName="PasswordReset">
                <PasswordReset />
              </FormErrorBoundary>
            } 
          />
          <Route 
            path="/" 
            element={user ? <Dashboard /> : (
              <FormErrorBoundary formName="Login">
                <Login />
              </FormErrorBoundary>
            )} 
          />
          <Route 
            path="/help" 
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <Help />
              </Suspense>
            }
          />
          <Route 
            path="/help/articles" 
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <ArticleListingPage />
              </Suspense>
            }
          />
          <Route 
            path="/help/articles/:articleId" 
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <ArticleDetailPage />
              </Suspense>
            }
          />
          <Route 
            path="/help/patient-management" 
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <PatientManagementModule />
              </Suspense>
            }
          />
          <Route 
            path="/help/employee-management" 
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <EmployeeManagementModule />
              </Suspense>
            }
          />
          <Route 
            path="/help/supervision-forms" 
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <SupervisionFormsModule />
              </Suspense>
            }
          />
          <Route 
            path="/help/reporting-analytics" 
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <ReportingAnalyticsModule />
              </Suspense>
            }
          />
          <Route 
            path="/help/user-management" 
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <UserManagementModule />
              </Suspense>
            }
          />
          <Route 
            path="/help/audit-compliance" 
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <AuditComplianceModule />
              </Suspense>
            }
          />
          <Route 
            path="/help/getting-started" 
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <GettingStartedTopic />
              </Suspense>
            }
          />
          <Route 
            path="/help/authentication-security" 
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <AuthenticationSecurityTopic />
              </Suspense>
            }
          />
          <Route
            path="/help/working-offline" 
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <WorkingOfflineTopic />
              </Suspense>
            }
          />
          <Route
            path="/help/common-workflows"
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <CommonWorkflowsTopic />
              </Suspense>
            }
          />
          <Route
            path="/help/troubleshooting"
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <TroubleshootingTopic />
              </Suspense>
            }
          />
          <Route
            path="/help/best-practices"
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <BestPracticesTopic />
              </Suspense>
            }
          />
        </Routes>
      </Suspense>
    </CriticalErrorBoundary>
  );
};

const App: React.FC = () => {
  // Initialize offline storage when app starts
  useEffect(() => {
    const initializeOfflineStorage = async () => {
      try {
        await offlineStorage.init();
        console.log('Offline storage initialized successfully');
      } catch (error) {
        errorHandler.handleStorageError(
          'offline_storage_initialization',
          error as Error,
          { context: 'app_startup' }
        );
      }
    };

    initializeOfflineStorage();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <CriticalErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <AuthProvider>
              <NetworkStatusProvider>
                <WorkflowProvider>
                  <SupervisionProvider>
                    <AppContent />
                    <OfflineIndicator floating />
                    <PWAInstallPrompt showAsFab={true} autoShow={true} />
                    <Toaster
                      position="top-right"
                      toastOptions={{
                        duration: 5000,
                        style: {
                          maxWidth: '500px',
                        },
                        success: {
                          iconTheme: {
                            primary: theme.palette.success.main,
                            secondary: theme.palette.success.contrastText,
                          },
                        },
                        error: {
                          iconTheme: {
                            primary: theme.palette.error.main,
                            secondary: theme.palette.error.contrastText,
                          },
                        },
                      }}
                    />
                  </SupervisionProvider>
                </WorkflowProvider>
              </NetworkStatusProvider>
            </AuthProvider>
        </Router>
        {/* React Query Devtools - only in true development environment */}
        {(() => {
          // Multiple checks to ensure this only shows in actual development
          const isDevelopment = process.env.NODE_ENV === 'development';
          const isLocalHost = window.location.hostname === 'localhost' || 
                             window.location.hostname === '127.0.0.1' ||
                             window.location.hostname === '0.0.0.0';
          const hasDevPort = window.location.port === '3000' || window.location.port === '3001';
          
          // Only show devtools if we're in development AND on localhost with dev port
          return isDevelopment && isLocalHost && hasDevPort;
        })() && <ReactQueryDevtools />}
      </QueryClientProvider>
      </CriticalErrorBoundary>
    </ThemeProvider>
  );
};

export default App; 